import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { staffApi, type StaffAgentMetrics, type StaffAgentProfile, type StaffPayout } from '../../api/staff';
import { Card } from '../../components/ui/Card';
import { formatMoney } from '../../utils/numbers';
import { formatProfessionalRange } from '../../config/support-pricing';
import './staff.css';

const LEVEL_LABELS: Record<string, string> = {
  bronze: 'Bronce',
  silver: 'Plata',
  gold: 'Oro',
  diamond: 'Diamante',
};

const ROLE_LABELS: Record<string, string> = {
  technician: 'Técnico',
  teamLead: 'Team Lead',
};

function payoutStatusLabel(status: StaffPayout['status']) {
  switch (status) {
    case 'paid':
      return 'Pagado';
    case 'processing':
      return 'En proceso';
    case 'cancelled':
      return 'Cancelado';
    default:
      return 'Pendiente de pago';
  }
}

export function StaffEarningsPage() {
  const [profile, setProfile] = useState<StaffAgentProfile | null>(null);
  const [metrics, setMetrics] = useState<StaffAgentMetrics | null>(null);
  const [payouts, setPayouts] = useState<StaffPayout[]>([]);
  const [period, setPeriod] = useState<'current_month' | 'last_month'>('current_month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([staffApi.getProfile(), staffApi.getMetrics(period), staffApi.getPayouts()])
      .then(([profileRes, metricsRes, payoutsRes]) => {
        setProfile(profileRes.agent);
        setMetrics(metricsRes.metrics);
        setPayouts(payoutsRes.payouts);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al cargar ganancias'))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) return <p>Cargando ganancias…</p>;
  if (error) return <p style={{ color: 'var(--color-danger)' }}>{error}</p>;
  if (!metrics || !profile) return <p>No se pudieron cargar las ganancias</p>;

  const level = profile.effective_level || profile.current_level;
  const activeClients = profile.active_referred_clients ?? profile.active_recurring_commissions ?? 0;

  return (
    <div>
      <div className="staff-page-header">
        <h1>Ganancias</h1>
        <div className="staff-earnings-period">
          <button
            type="button"
            className={period === 'current_month' ? 'is-active' : ''}
            onClick={() => setPeriod('current_month')}
          >
            Mes actual
          </button>
          <button
            type="button"
            className={period === 'last_month' ? 'is-active' : ''}
            onClick={() => setPeriod('last_month')}
          >
            Mes anterior
          </button>
        </div>
      </div>

      <div className="staff-earnings-grid">
        <Card title="Tu perfil profesional">
          <p style={{ margin: '0 0 0.5rem' }}>
            Nivel <strong>{LEVEL_LABELS[level] ?? level}</strong>
            {activeClients > 0 ? ` · ${activeClients} clientes activos referidos` : ''}
          </p>
          <p className="muted" style={{ margin: 0 }}>
            Rol: {ROLE_LABELS[profile.professional_role ?? 'technician'] ?? 'Técnico'}
          </p>
          <p className="muted" style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
            Las horas de soporte se liquidan según tarifa estándar ({formatProfessionalRange('standard')}) o urgente ({formatProfessionalRange('urgent')}).
            Las bonificaciones por referidos se gestionan en{' '}
            <Link to="/staff/referidos">Referidos</Link>.
          </p>
        </Card>

        <Card title="Pendiente de cobro">
          <div className="staff-earnings-breakdown">
            <div>
              <span className="muted">Soporte</span>
              <strong>{formatMoney(metrics.pending.tickets)}</strong>
            </div>
            <div>
              <span className="muted">Valoraciones</span>
              <strong>{formatMoney(metrics.pending.ratings)}</strong>
            </div>
            <div>
              <span className="muted">Ventas verificadas</span>
              <strong>{formatMoney(metrics.pending.commission)}</strong>
            </div>
            <div className="staff-earnings-total">
              <span>Total pendiente</span>
              <strong>{formatMoney(metrics.pending.total)}</strong>
            </div>
          </div>
        </Card>
      </div>

      <div className="staff-earnings-grid" style={{ marginTop: '1rem' }}>
        <Card title="Ganancias del periodo">
          <div className="staff-earnings-breakdown">
            <div>
              <span className="muted">Por soporte ({metrics.tickets.resolved} tickets)</span>
              <strong>{formatMoney(metrics.earnings.tickets)}</strong>
            </div>
            <div>
              <span className="muted">Por valoraciones</span>
              <strong>{formatMoney(metrics.earnings.ratings)}</strong>
            </div>
            <div>
              <span className="muted">Por ventas ({metrics.sales.count})</span>
              <strong>{formatMoney(metrics.earnings.commission)}</strong>
            </div>
            <div className="staff-earnings-total">
              <span>Total periodo</span>
              <strong>{formatMoney(metrics.earnings.total)}</strong>
            </div>
          </div>
        </Card>

        <Card title="Actividad">
          <div className="staff-earnings-breakdown">
            <div>
              <span className="muted">Tickets totales</span>
              <strong>{metrics.tickets.total}</strong>
            </div>
            <div>
              <span className="muted">Calificación media</span>
              <strong>{metrics.tickets.average_rating ?? '—'}</strong>
            </div>
            <div>
              <span className="muted">Ventas registradas</span>
              <strong>{formatMoney(metrics.sales.total_amount)}</strong>
            </div>
          </div>
        </Card>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <Card title="Historial de liquidaciones">
          {payouts.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              Aún no tienes liquidaciones registradas.
            </p>
          ) : (
            <div className="staff-payout-list">
              {payouts.map((payout) => (
                <div key={payout.id} className="staff-payout-item">
                  <div>
                    <strong>
                      {new Date(payout.period_start).toLocaleDateString('es-ES')} –{' '}
                      {new Date(payout.period_end).toLocaleDateString('es-ES')}
                    </strong>
                    <p className="muted" style={{ margin: '0.2rem 0 0', fontSize: '0.85rem' }}>
                      {payoutStatusLabel(payout.status)}
                      {payout.paid_at
                        ? ` · ${new Date(payout.paid_at).toLocaleDateString('es-ES')}`
                        : ''}
                      {payout.referral_milestone_bonus
                        ? ` · Hitos: ${formatMoney(payout.referral_milestone_bonus)}`
                        : ''}
                    </p>
                  </div>
                  <strong>{formatMoney(payout.total_amount)}</strong>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
